import React from 'react';
import { Settings as SettingsIcon, User, Bell, Shield, Database, Palette } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';

const Settings = () => {
  const { user } = useAuth();

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Settings</h1>
        <p className="text-slate-600 mt-1">Manage your account and application settings</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sidebar */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 h-fit">
          <nav className="space-y-1">
            <button className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg bg-indigo-50 text-indigo-700 font-medium">
              <User className="w-5 h-5" />
              Profile
            </button>
            <button className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-600 hover:bg-slate-50">
              <Bell className="w-5 h-5" />
              Notifications
            </button>
            <button className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-600 hover:bg-slate-50">
              <Shield className="w-5 h-5" />
              Security
            </button>
            <button className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-600 hover:bg-slate-50">
              <Database className="w-5 h-5" />
              Data
            </button>
            <button className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-slate-600 hover:bg-slate-50">
              <Palette className="w-5 h-5" />
              Appearance
            </button>
          </nav>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Profile Section */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Profile Information</h2>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input id="name" defaultValue={user?.full_name} data-testid="settings-name-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input id="email" type="email" defaultValue={user?.email} disabled data-testid="settings-email-input" />
                <p className="text-xs text-slate-500">Email cannot be changed</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Input id="role" defaultValue={user?.role} disabled className="capitalize" />
              </div>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-profile-btn">
                Save Changes
              </Button>
            </div>
          </div>

          {/* Notifications Section */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Notification Preferences</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">Email Notifications</p>
                  <p className="text-sm text-slate-500">Receive email updates about important events</p>
                </div>
                <Switch defaultChecked data-testid="email-notifications-switch" />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">Fee Reminders</p>
                  <p className="text-sm text-slate-500">Get notified about pending fee payments</p>
                </div>
                <Switch defaultChecked data-testid="fee-reminders-switch" />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">Exam Schedules</p>
                  <p className="text-sm text-slate-500">Notifications about upcoming exams</p>
                </div>
                <Switch defaultChecked data-testid="exam-notifications-switch" />
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-900">Attendance Alerts</p>
                  <p className="text-sm text-slate-500">Daily attendance summary notifications</p>
                </div>
                <Switch data-testid="attendance-notifications-switch" />
              </div>
            </div>
          </div>

          {/* Security Section */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Security</h2>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current_password">Current Password</Label>
                <Input id="current_password" type="password" data-testid="current-password-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new_password">New Password</Label>
                <Input id="new_password" type="password" data-testid="new-password-input" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm_password">Confirm New Password</Label>
                <Input id="confirm_password" type="password" data-testid="confirm-password-input" />
              </div>
              <Button variant="outline" data-testid="change-password-btn">Change Password</Button>
            </div>
          </div>

          {/* School Information */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">School Information</h2>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="school_name">School Name</Label>
                <Input id="school_name" defaultValue="Academion School" data-testid="school-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="academic_year">Current Academic Year</Label>
                  <Input id="academic_year" defaultValue="2024" data-testid="academic-year-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="current_term">Current Term</Label>
                  <Input id="current_term" defaultValue="Term 1" data-testid="current-term-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="currency">Currency</Label>
                <Input id="currency" defaultValue="KES" data-testid="currency-input" />
              </div>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-school-settings-btn">
                Save School Settings
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
